import service from '@/services';
import config from '@/services/config';
import { AxiosError } from 'axios';

export type MemberProfile = {
  id: string;
  client_id: string;
  name: string;
  email: string;
  role: string;
  incomplete_profile?: boolean;
  firm_data_advisor_id?: string;
  firm_data_context?: FirmDataAccessContext | null;
};

export type FirmDataAuthorizedInvestorContext = {
  firm_id: string;
  id: string;
  status: string;
};

export type FirmDataFirmContext = {
  firm_id: string;
  subject_type: string;
  investors: FirmDataAuthorizedInvestorContext[];
};

export type FirmDataAccessContext = {
  consumer_type: string;
  firms: FirmDataFirmContext[];
};

export const AuthErrorCodes = {
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  USER_NOT_FOUND: 'USER_NOT_FOUND',
  USER_NOT_CONFIRMED: 'USER_NOT_CONFIRMED',
  RECAPTCHA_FAILED: 'RECAPTCHA_FAILED',
  INVALID_SESSION: 'INVALID_SESSION',
  INVALID_CODE: 'INVALID_CODE',
  CODE_EXPIRED: 'CODE_EXPIRED',
  INVALID_PASSWORD: 'INVALID_PASSWORD',
  PASSWORDS_DO_NOT_MATCH: 'PASSWORDS_DO_NOT_MATCH',
  INVALID_PARAMETERS: 'INVALID_PARAMETERS',
  RATE_LIMITED: 'RATE_LIMITED',
  EMAIL_EXISTS: 'EMAIL_EXISTS',
} as const;

export type AuthErrorCode = (typeof AuthErrorCodes)[keyof typeof AuthErrorCodes];

export type LoginErrorCode =
  | typeof AuthErrorCodes.INVALID_CREDENTIALS
  | typeof AuthErrorCodes.USER_NOT_FOUND
  | typeof AuthErrorCodes.USER_NOT_CONFIRMED
  | typeof AuthErrorCodes.RECAPTCHA_FAILED;

export type SignupErrorCode =
  | typeof AuthErrorCodes.INVALID_PARAMETERS
  | typeof AuthErrorCodes.RECAPTCHA_FAILED
  | typeof AuthErrorCodes.EMAIL_EXISTS;

export type SignupConfirmErrorCode =
  | typeof AuthErrorCodes.INVALID_PARAMETERS
  | typeof AuthErrorCodes.INVALID_CODE
  | typeof AuthErrorCodes.CODE_EXPIRED;

export type ResendConfirmationCodeErrorCode =
  | typeof AuthErrorCodes.USER_NOT_FOUND
  | typeof AuthErrorCodes.RATE_LIMITED;

export type SetNewPasswordErrorCode =
  | typeof AuthErrorCodes.PASSWORDS_DO_NOT_MATCH
  | typeof AuthErrorCodes.INVALID_PASSWORD
  | typeof AuthErrorCodes.INVALID_SESSION
  | typeof AuthErrorCodes.CODE_EXPIRED;

export type ForgetPasswordConfirmErrorCode =
  | typeof AuthErrorCodes.INVALID_CODE
  | typeof AuthErrorCodes.CODE_EXPIRED
  | typeof AuthErrorCodes.INVALID_PASSWORD
  | typeof AuthErrorCodes.USER_NOT_FOUND;

type LoginNewPasswordRequired = {
  kind: 'new_password_required';
  session_id: string;
};

type LoginSuccess = {
  kind: 'success';
  access_token: string;
  user: MemberProfile;
  firm_data_context?: FirmDataAccessContext | null;
};

type ProfileIncomplete = {
  kind: 'profile_incomplete';
  access_token: string;
  user: MemberProfile;
  incomplete_profile: true;
  firm_data_context?: FirmDataAccessContext | null;
};

type LoginResponse = LoginSuccess | LoginNewPasswordRequired | ProfileIncomplete;
export type AuthenticatedAuthResponse = LoginSuccess | ProfileIncomplete;

type LegacyLoginError = {
  kind: 'WRONG_CREDENTIAL' | 'USER_NOT_FOUND';
  message: string;
};

type LegacyLoginResponse = LoginResponse | LegacyLoginError;

interface AuthApiErrorResponse<T = AuthErrorCode> {
  detail: {
    status: 'error';
    error_code: T;
    message: string;
    details?: unknown;
  };
}

export class AuthError extends Error {
  readonly name = 'AuthError';

  constructor(
    public readonly code: AuthErrorCode | string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
  }

  static fromResponse<T extends AuthErrorCode>(response: AuthApiErrorResponse<T>): AuthError {
    return new AuthError(
      response.detail.error_code,
      response.detail.message,
      response.detail.details,
    );
  }
}

export function isAuthError(error: unknown): error is AuthError {
  return error instanceof AuthError;
}

export function userWithFirmDataContext<T extends MemberProfile>(response: {
  user: T;
  firm_data_context?: FirmDataAccessContext | null;
}): T {
  if (!response.firm_data_context) {
    return response.user;
  }

  return {
    ...response.user,
    firm_data_context: response.firm_data_context,
  };
}

function isApiErrorResponse(data: unknown): data is AuthApiErrorResponse {
  if (typeof data !== 'object' || data === null || !('detail' in data)) {
    return false;
  }

  const detail = (data as { detail: unknown }).detail;

  return (
    typeof detail === 'object' &&
    detail !== null &&
    typeof (detail as { error_code?: unknown }).error_code === 'string' &&
    typeof (detail as { message?: unknown }).message === 'string'
  );
}

function isLegacyLoginError(data: unknown): data is LegacyLoginError {
  if (typeof data !== 'object' || data === null) return false;
  const kind = (data as { kind?: unknown }).kind;
  return (
    (kind === 'WRONG_CREDENTIAL' || kind === 'USER_NOT_FOUND') &&
    typeof (data as { message?: unknown }).message === 'string'
  );
}

function legacyLoginErrorCode(kind: LegacyLoginError['kind']): LoginErrorCode {
  return kind === 'USER_NOT_FOUND'
    ? AuthErrorCodes.USER_NOT_FOUND
    : AuthErrorCodes.INVALID_CREDENTIALS;
}

function handleApiError(error: unknown, options?: { allowRecaptchaFallback?: boolean }): never {
  if (error instanceof AxiosError) {
    const legacyDetail = error.response?.data?.detail;
    if (isLegacyLoginError(legacyDetail)) {
      throw new AuthError(legacyLoginErrorCode(legacyDetail.kind), legacyDetail.message);
    }

    if (options?.allowRecaptchaFallback && error.response?.status === 403) {
      const responseData = error.response.data;
      if (!isApiErrorResponse(responseData)) {
        const message =
          typeof responseData?.detail?.message === 'string'
            ? responseData.detail.message
            : typeof responseData?.detail === 'string'
              ? responseData.detail
              : 'reCAPTCHA verification failed';

        throw new AuthError(AuthErrorCodes.RECAPTCHA_FAILED, message);
      }
    }

    if (error.response?.data && isApiErrorResponse(error.response.data)) {
      throw AuthError.fromResponse(error.response.data);
    }
  }
  throw error;
}

async function loginWithoutRecaptcha(loginObj: {
  email: string;
  password: string;
}): Promise<LoginResponse> {
  try {
    const response = await service.post<LegacyLoginResponse>(
      '/authenticate',
      {
        email: loginObj.email,
        password: loginObj.password,
      },
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      },
    );

    if (isLegacyLoginError(response)) {
      throw new AuthError(legacyLoginErrorCode(response.kind), response.message);
    }

    return response;
  } catch (error) {
    if (isAuthError(error)) {
      throw error;
    }

    handleApiError(error);
  }
}

export async function login(loginObj: {
  email: string;
  password: string;
  recaptcha_token: string;
}): Promise<LoginResponse> {
  if (config.disableRecaptcha) {
    return loginWithoutRecaptcha(loginObj);
  }

  try {
    return await service.post<LoginResponse>('/v2/auth/authenticate', loginObj);
  } catch (error) {
    handleApiError(error, { allowRecaptchaFallback: true });
  }
}

type SignupResponse = {
  kind?: 'new_signup' | 'existing_unconfirmed' | 'setup_instructions_sent';
  cognito_session: string;
  next_step?: 'signup_confirm' | 'complete_signup' | 'check_email';
  delivery_medium?: string;
  destination?: string;
};

export async function signup(signupObj: {
  email: string;
  password: string;
  recaptcha_token: string;
}): Promise<SignupResponse> {
  try {
    const response = await service.post<SignupResponse>('/v2/auth/signup', signupObj);

    if (
      !response.cognito_session?.trim() &&
      response.next_step !== 'complete_signup' &&
      response.next_step !== 'check_email'
    ) {
      throw new AuthError(
        AuthErrorCodes.EMAIL_EXISTS,
        'An account with this email already exists. Please log in or reset your password.',
      );
    }

    return response;
  } catch (error) {
    if (isAuthError(error)) {
      throw error;
    }

    handleApiError(error, { allowRecaptchaFallback: true });
  }
}

type SignupConfirmRequest = {
  email: string;
  confirmation_code: string;
  cognito_session: string;
  invite_token?: string;
};

type SignupConfirmResponse = AuthenticatedAuthResponse;

export async function signupConfirm(
  signupConfirmObj: SignupConfirmRequest,
): Promise<SignupConfirmResponse> {
  try {
    return await service.post<SignupConfirmResponse>('/v2/auth/signup-confirm', signupConfirmObj);
  } catch (error) {
    handleApiError(error);
  }
}

type ResendConfirmationCodeRequest = {
  email: string;
};

type ResendConfirmationCodeResponse = {
  delivery_medium: string;
  destination: string;
};

export async function resendConfirmationCode(
  resendConfirmationCodeObj: ResendConfirmationCodeRequest,
): Promise<ResendConfirmationCodeResponse> {
  try {
    return await service.post<ResendConfirmationCodeResponse>(
      '/v2/auth/resend-confirmation-code',
      resendConfirmationCodeObj,
    );
  } catch (error) {
    handleApiError(error);
  }
}

type SetNewPasswordRequest = {
  session_id: string;
  email: string;
  password: string;
  confirm: string;
};

type SetNewPasswordResponse = LoginSuccess;

export async function setNewPassword(
  setNewPasswordObj: SetNewPasswordRequest,
): Promise<SetNewPasswordResponse> {
  try {
    return await service.post<SetNewPasswordResponse>('/v2/auth/new-password', setNewPasswordObj);
  } catch (error) {
    handleApiError(error);
  }
}

export async function logout(): Promise<void> {
  try {
    await service.post('/v2/auth/sign-out');
  } catch (error) {
    handleApiError(error);
  }
}

export async function forgetPassword(forgetPasswordObj: { email: string }): Promise<void> {
  try {
    await service.post('/v2/auth/forget-password', forgetPasswordObj);
  } catch (error) {
    handleApiError(error);
  }
}

export async function forgetPasswordConfirm(forgetPasswordConfirmObj: {
  email: string;
  confirmation_code: string;
  password: string;
}): Promise<void> {
  try {
    await service.post('/v2/auth/forget-password-confirm', forgetPasswordConfirmObj);
  } catch (error) {
    handleApiError(error);
  }
}

export type CompleteSignupErrorCode =
  | typeof AuthErrorCodes.INVALID_CODE
  | typeof AuthErrorCodes.CODE_EXPIRED
  | typeof AuthErrorCodes.USER_NOT_FOUND
  | typeof AuthErrorCodes.INVALID_PARAMETERS
  | typeof AuthErrorCodes.INVALID_CREDENTIALS
  | typeof AuthErrorCodes.RATE_LIMITED;

export async function completeSignup(completeSignupObj: {
  email: string;
  confirmation_code: string;
  password: string;
  invite_token?: string;
}): Promise<SignupConfirmResponse> {
  try {
    return await service.post<SignupConfirmResponse>('/v2/auth/complete-signup', completeSignupObj);
  } catch (error) {
    handleApiError(error);
  }
}

type CompleteProfileRequest = {
  company_name: string;
  member_name: string;
};

/** Normalized result after parsing backend payload shapes */
export type CompleteProfileResult = {
  user: MemberProfile;
  /** Present when backend issues a new token; otherwise keep existing session token */
  access_token?: string;
};

function isMemberProfileLike(value: unknown): value is MemberProfile {
  if (value === null || typeof value !== 'object') return false;
  const o = value as Record<string, unknown>;
  return (
    typeof o.id === 'string' &&
    typeof o.client_id === 'string' &&
    typeof o.email === 'string' &&
    typeof o.name === 'string' &&
    typeof o.role === 'string'
  );
}

/**
 * Backend may return either:
 * - Envelope unwrapped to `data`: the member object directly (success path you described)
 * - `{ access_token?, user: MemberProfile }` inside `data`
 */
function parseCompleteProfileResponse(payload: unknown): CompleteProfileResult {
  if (payload === null || typeof payload !== 'object') {
    throw new Error('Invalid complete-profile response');
  }
  const root = payload as Record<string, unknown>;

  if ('user' in root && isMemberProfileLike(root.user)) {
    return {
      user: root.user,
      access_token: typeof root.access_token === 'string' ? root.access_token : undefined,
    };
  }

  if (isMemberProfileLike(payload)) {
    return {
      user: payload,
      access_token: typeof root.access_token === 'string' ? root.access_token : undefined,
    };
  }

  throw new Error('Invalid complete-profile response');
}

export async function completeProfile(
  completeProfileObj: CompleteProfileRequest,
): Promise<CompleteProfileResult> {
  try {
    const raw = await service.post<unknown>('/v2/auth/complete-profile', completeProfileObj);
    return parseCompleteProfileResponse(raw);
  } catch (error) {
    handleApiError(error);
  }
}
